import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { useAllEdges, useConcepts, useLessons, useModules, useRecord } from '@/db/hooks'
import { PageTitle, cx } from '@/components/ui'
import { courseConcepts, knownOf, outlineOf } from '@/lib/learn'
import type { CourseResource } from '@/kinds/learn/apply'
import { Gone } from '../NotFound'
import { FamiliarityWord } from './parts'

/** A course's outline: its modules fold open to their lessons, and each lesson to the concept it teaches. */
export const CourseView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const course = useRecord(id) as CourseResource | null | undefined
  const modules = useModules() ?? []
  const lessons = useLessons() ?? []
  const concepts = useConcepts() ?? []
  const edges = useAllEdges()
  const [open, setOpen] = useState<Set<string>>(new Set())
  if (course === undefined) return null
  if (!course || course.deleted_at || course.kind !== 'Course')
    return <Gone kind="course" deleted={!!course?.deleted_at} />
  const tree = outlineOf(course.id, { modules, lessons, concepts, edges })
  const { known, total } = knownOf(courseConcepts(tree))
  const toggle = (mid: string) =>
    setOpen((cur) => {
      const next = new Set(cur)
      if (!next.delete(mid)) next.add(mid)
      return next
    })
  return (
    <div data-testid="course">
      <Link to="/learn" className="inline-flex items-center min-h-11 text-sm text-muted hover:text-fg">
        ← Learn
      </Link>
      <PageTitle>{course.spec.title}</PageTitle>
      {course.spec.goal && <p className="text-[15px] text-fg-2 -mt-2 mb-3">{course.spec.goal}</p>}
      <p className="text-xs text-faint mb-4">
        {known} of {total} known
      </p>
      <ul className="flex flex-col gap-3">
        {tree.map(({ module, lessons: ls }) => {
          const on = open.has(module.id)
          return (
            <li key={module.id} className="group-list" data-testid="course-module">
              <button
                type="button"
                onClick={() => toggle(module.id)}
                aria-expanded={on}
                className="w-full flex items-center justify-between gap-3 px-3.5 min-h-12 text-left"
              >
                <span className="text-[15px] font-medium">{module.spec.title}</span>
                <ChevronDown
                  size={18}
                  className={cx('text-faint shrink-0 transition-transform', on && 'rotate-180')}
                  aria-hidden
                />
              </button>
              {on &&
                ls.map(({ lesson, concepts: taught }) => {
                  const c = taught[0]
                  const row = (
                    <>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] truncate">{lesson.spec.title}</span>
                        {c?.spec.one_liner && (
                          <span className="block text-xs text-muted truncate">{c.spec.one_liner}</span>
                        )}
                      </span>
                      {c && <FamiliarityWord state={c.status.familiarity} />}
                    </>
                  )
                  const cls = 'flex items-center gap-3 px-3.5 min-h-12 py-1.5'
                  return c ? (
                    <Link key={lesson.id} to="/learn/concept/$id" params={{ id: c.id }} className={cls}>
                      {row}
                    </Link>
                  ) : (
                    <div key={lesson.id} className={cls}>
                      {row}
                    </div>
                  )
                })}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
